import {
  SkyAccordion,
  SkyAccordionItem,
  SkyBadge,
  SkyButton,
  SkyDataGrid,
  SkyText,
  type SkyDataGridColumn,
} from "@skypointcloud/skypoint-ui";
import { useMemo, useState } from "react";
import { Link } from "wouter";
import type {
  TransformationGraph,
  TransformationGraphTest,
  DbtJobRunRow,
  DbtJobView,
} from "@shared/types/transformations";
import { useDbtJobRuns } from "@/hooks/use-dbt-jobs";
import { usePermissions } from "@/hooks/use-permissions";
import { PlayIcon, RetryIcon } from "@/lib/icons";
import { absolutePath } from "@/lib/routes";
import {
  TEST_RESULTS,
  groupTests,
  testResultOf,
  testTally,
  type TestGroup,
  type TestResult,
} from "@/lib/transformations/modelGroups";
import { isRunInFlight } from "@/lib/transformations/runOutcome";
import { CauseBlock, CauseNotClassified, failuresUnit } from "./CauseBlock";
import { CountPill } from "./CountPill";
import { RunDbtJobsDialog } from "./RunDbtJobsDialog";
import {
  Dash,
  GraphLoading,
  GraphReadError,
  ProjectEmptyState,
  SourceLink,
  WalkedBackNote,
  useLandingParam,
  type GraphState,
} from "./TransformationModelsView";
import {
  SOURCE_SQL_TENANT_ADMIN_ONLY,
  TransformationSqlDialog,
  type TransformationSqlTarget,
} from "./TransformationSqlDialog";
import { formatInstant, instantHoverTitle } from "@/lib/time/displayTime";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { useNow } from "@/hooks/use-now";
import { endedWhen } from "@/lib/runs/runActivity";
import { byCodeUnit } from "@shared/sort";

/**
 * The Transformations landing's Tests view (Session 11, ux-s11-transformations.md §3.2; Studio's
 * DBT Tests tab, shots 7 and 8): every data test and unit test of the project with its latest
 * result over the last 30 runs, grouped exactly as Studio groups them (models A→Z, then "Custom
 * SQL checks", "Sources", "Unattributed"), unhealthy groups first; all folded unless there is only one.
 *
 * Summary toggles filter the list (`?result=`, so the landing's "Show tests" bar lands filtered);
 * each is its label and a count pill (`CountPill`), the pressed one the active filter.
 * A result from an older run than the graph's says so. Query opens the audited SQL of the test in
 * the run of its last result (ruling §8.5). "Run tests" maps to the existing Run transformations
 * (ruling §8.6): it opens `RunDbtJobsDialog` preselected with the enabled transformations
 * whose Run tests is on; there is no tests-only run. Viewer-safe: "Run tests" is for `runs.start`.
 */

export interface TransformationTestsViewProps {
  graph: TransformationGraph | undefined;
  graphState: GraphState;
  onRetry(): void;
  canStart: boolean;
  transformations: DbtJobView[];
  onRefresh(): void;
  /** As on the Models view: the landing's New dbt job, for `dbt_jobs.write`. */
  onNewDbtJob?: () => void;
}

const RESULT_LABELS: Record<TestResult, string> = {
  passed: "Passed",
  failed: "Failed",
  warned: "Warned",
  errored: "Errored",
  skipped: "Skipped",
  not_run: "No recent result",
};

/** The sentence when a result filter matches no test (review nit: "No test is passed." read badly). */
const NO_MATCH: Record<TestResult, string> = {
  passed: "No test passed.",
  failed: "No test failed.",
  warned: "No test warned.",
  errored: "No test errored.",
  skipped: "No test was skipped.",
  not_run: "Every test has a result in the last 30 runs.",
};

const RESULT_COLORS: Record<TestResult, "success" | "danger" | "warning" | "subtle"> = {
  passed: "success",
  failed: "danger",
  warned: "warning",
  errored: "danger",
  skipped: "subtle",
  not_run: "subtle",
};

/**
 * The summary toggles, in Studio's order plus Skipped (UX §3.2) and No recent result (walk
 * 2026-10-02, D5: a test with no result in the last 30 runs was in Total and in no chip). #1213
 * round 4: this view's basis is the last 30 runs, so its word is "No recent result"; the facts
 * strip's "Not in this run" is the newest run's basis, and the two never share a word.
 */
const SUMMARY: TestResult[] = ["passed", "failed", "warned", "errored", "skipped", "not_run"];

const KIND_WORDS: Record<string, string> = {
  not_null: "not null",
  unique: "unique",
  accepted_values: "accepted values",
  relationships: "relationships",
  singular: "custom SQL",
  generic: "generic",
  unit: "unit test",
};

/** A test's kind in words ("unit test" for a dbt unit test). */
export function testKindWord(test: Pick<TransformationGraphTest, "kind" | "uniqueId">): string {
  if (test.kind) return KIND_WORDS[test.kind] ?? test.kind;
  return test.uniqueId.startsWith("unit_test.") ? "unit test" : "—";
}

/** What a test's row reads: its latest result across runs, else the graph run's own. */
interface TestView {
  test: TransformationGraphTest;
  status: string | null;
  failures: number | null;
  runId: string | null;
  at: string | null;
  older: boolean;
  cause: TransformationGraphTest["cause"];
}

function viewOf(test: TransformationGraphTest, graph: TransformationGraph): TestView {
  const last = test.lastResult ?? null;
  const graphRun = graph.run;
  if (last) {
    return {
      test,
      status: last.status,
      failures: last.failures,
      runId: last.runId,
      at: last.at,
      older: Boolean(graphRun && last.runId !== graphRun.runId),
      cause: last.cause,
    };
  }
  return {
    test,
    status: test.status,
    failures: test.failures,
    runId: test.status !== null ? (graphRun?.runId ?? null) : null,
    at: test.status !== null ? (graphRun?.finishedAt ?? graphRun?.startedAt ?? null) : null,
    older: false,
    cause: test.cause,
  };
}

/** A test's view with the keys `groupTests` reads. */
type TestItem = TestView & { uniqueId: string; name: string; dependsOn: string[]; kind?: string };

interface TestRow {
  id: string;
  name: string;
  view: TestView;
  result: TestResult;
}

function TestsGrid({
  group,
  graph,
  onQuery,
  testId,
}: {
  group: TestGroup<TestItem>;
  graph: TransformationGraph;
  onQuery(target: TransformationSqlTarget, opener: HTMLElement | null): void;
  testId: string;
}) {
  const zone = useDisplayTz();
  // Owner 2026-10-14 (#1002): the Query dialog reads only for Tenant admins and above; below it
  // the cell is a dash with the owner's sentence, never a control that can only refuse.
  const mayQuery = usePermissions().can("instance.binding.identity.read");
  const data: TestRow[] = group.members.map((member) => ({
    id: member.test.test.uniqueId,
    name: member.test.test.name,
    view: member.test,
    result: member.result,
  }));
  const nonModel = group.kind !== "model";
  const columns: SkyDataGridColumn<TestRow>[] = [
    {
      key: "name",
      header: "Test",
      width: nonModel ? "17%" : "21%",
      renderCell: (_value, row) => (
        <div className="flex min-w-0 flex-col gap-1">
          <SkyText size={200} font="monospace" truncate title={row.view.test.uniqueId}>
            {row.name}
          </SkyText>
          {row.view.cause || isProblem(row.result) ? (
            <div data-testid={`transformation-test-cause-${row.name}`}>
              {row.view.cause ? (
                <CauseBlock
                  cause={row.view.cause}
                  compact
                  testId={`transformation-test-cause-block-${row.name}`}
                  whyOnlyWhenGeneric
                />
              ) : (
                <CauseNotClassified testId={`transformation-test-cause-none-${row.name}`} />
              )}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      key: "kind",
      header: "Kind",
      width: "11%",
      renderCell: (_value, row) => (
        <SkyText size={200} testId={`transformation-test-kind-${row.name}`}>
          {testKindWord(row.view.test)}
        </SkyText>
      ),
    },
    ...(nonModel
      ? [
          {
            key: "model",
            header: "Model",
            width: "11%",
            renderCell: (_value: unknown, row: TestRow) => {
              const model = graph.nodes.find((node) =>
                row.view.test.dependsOn.includes(node.uniqueId),
              );
              return (
                <SkyText size={200} font="monospace" truncate>
                  {model?.name ?? "—"}
                </SkyText>
              );
            },
          } satisfies SkyDataGridColumn<TestRow>,
        ]
      : []),
    {
      key: "column",
      header: "Column",
      width: "10%",
      renderCell: (_value, row) => (
        <SkyText size={200} font="monospace" color={row.view.test.column ? "default" : "muted"}>
          {row.view.test.column ?? "—"}
        </SkyText>
      ),
    },
    {
      key: "result",
      header: "Result",
      width: "13%",
      renderCell: (_value, row) => (
        <div
          className="flex flex-wrap items-center gap-1"
          data-testid={`transformation-test-result-${row.name}`}
        >
          <SkyBadge appearance="tint" color={RESULT_COLORS[row.result]}>
            {RESULT_LABELS[row.result]}
          </SkyBadge>
          {row.view.test.severity === "warn" ? (
            <SkyBadge appearance="outline" color="subtle">
              Warn only
            </SkyBadge>
          ) : null}
        </div>
      ),
    },
    {
      key: "failures",
      header: "Failures",
      width: "9%",
      align: "right",
      renderCell: (_value, row) => (
        <SkyText size={200} font="numeric">
          {row.view.failures === null
            ? "—"
            : `${row.view.failures} ${failuresUnit({ cause: row.view.cause, kind: row.view.test.kind }, row.view.failures)}`}
        </SkyText>
      ),
    },
    {
      key: "lastRun",
      header: "Last run",
      width: "15%",
      renderCell: (_value, row) =>
        row.view.runId && row.view.at ? (
          <div className="flex flex-col" data-testid={`transformation-test-last-run-${row.name}`}>
            <Link
              href={absolutePath(`/runs/${row.view.runId}`)}
              className="underline-offset-2 hover:underline"
              aria-label={`Open run ${row.view.runId}`}
            >
              <SkyText size={200} color="accent">
                <span title={instantHoverTitle(row.view.at, zone)}>
                  {formatInstant(row.view.at, zone, "seconds")}
                </span>
              </SkyText>
            </Link>
            {row.view.older ? (
              <SkyText size={200} color="muted">
                from an older run
              </SkyText>
            ) : null}
          </div>
        ) : (
          <SkyText size={200} color="muted">
            Not in the last 30 runs
          </SkyText>
        ),
    },
    {
      key: "query",
      header: "Query",
      width: "7%",
      renderCell: (_value, row) =>
        row.view.runId && !mayQuery ? (
          <Dash
            label={`No query: ${SOURCE_SQL_TENANT_ADMIN_ONLY}`}
            hint={SOURCE_SQL_TENANT_ADMIN_ONLY}
          />
        ) : row.view.runId ? (
          <SkyButton
            appearance="subtle"
            size="small"
            aria-label={`Query of ${row.name}`}
            testId={`transformation-test-query-${row.name}`}
            onClick={(event) =>
              onQuery(
                { runId: row.view.runId as string, nodeId: row.view.test.uniqueId, name: row.name },
                event.currentTarget as HTMLElement,
              )
            }
          >
            Query
          </SkyButton>
        ) : (
          <SkyText size={200} color="muted" aria-label="No query: the test has not run">
            —
          </SkyText>
        ),
    },
    {
      // Review S2 (UX §3.2, contract §6.3): the file the test is defined in, at the commit the
      // graph's run built (the Models view's rule).
      key: "source",
      header: "Source",
      width: "7%",
      renderCell: (_value, row) => (
        <SourceLink
          graph={graph}
          path={row.view.test.path}
          name={row.name}
          testId={`transformation-test-source-${row.name}`}
        />
      ),
    },
  ];
  return (
    <section aria-label={`Tests in ${group.label}`} data-testid={testId}>
      <SkyDataGrid<TestRow>
        data={data}
        columns={columns}
        getRowId={(row) => row.id}
        pagination={data.length > 25}
        pageSize={25}
        emptyMessage="No test matches the filter"
      />
    </section>
  );
}

function isProblem(result: TestResult): boolean {
  return result === "failed" || result === "errored" || result === "warned";
}

function groupBadge(group: TestGroup<unknown>) {
  // The failing count reads in the heading's words ("· 2 failing"), so no badge repeats it.
  if (group.failing > 0) return null;
  if (group.warning > 0) {
    return (
      <SkyBadge appearance="tint" color="warning">
        {`${group.warning} warning`}
      </SkyBadge>
    );
  }
  if (group.passing > 0) {
    return (
      <SkyBadge appearance="tint" color="success">
        {`${group.passing} passing`}
      </SkyBadge>
    );
  }
  // Nothing failed, warned or passed (S11 PR review N2): the tests did not run (Run tests off, or
  // never in the last 30 runs) or were skipped. A neutral badge in the result's own word, never a
  // green "0 passing".
  const notRun = group.members.filter((member) => member.result === "not_run").length;
  const skipped = group.members.length - notRun;
  return (
    <SkyBadge appearance="tint" color="subtle">
      {notRun > 0 ? `${notRun} with no recent result` : `${skipped} skipped`}
    </SkyBadge>
  );
}

/** `?result=`: a known test result, else every result. */
function testResultParam(resultParam: string | null): TestResult | null {
  return (TEST_RESULTS as readonly string[]).includes(resultParam ?? "")
    ? (resultParam as TestResult)
    : null;
}

function resultsHeader(newestResult: string | undefined, zone: string, now: Date): string {
  return newestResult
    ? `Newest result ${endedWhen(newestResult, zone, now)} · latest result of each test over the last 30 runs`
    : "Latest result of each test over the last 30 runs";
}

function newestInFlight(newest: DbtJobRunRow | undefined): boolean {
  return newest ? isRunInFlight(newest) : false;
}

/** The tests by model, each group folded under its heading and badge; or that none matches. */
function TestGroups({
  groups,
  selected,
  openItems,
  onToggle,
  graph,
  onQuery,
}: {
  groups: ReturnType<typeof groupTests<TestItem>>;
  selected: TestResult | null;
  openItems: string[];
  onToggle: (items: string[]) => void;
  graph: TransformationGraph;
  onQuery: (target: TransformationSqlTarget, opener: HTMLElement | null) => void;
}) {
  if (groups.length === 0) {
    return (
      <SkyText size={200} color="muted" testId="transformation-tests-no-match">
        {selected ? NO_MATCH[selected] : "No test matches the filter."}
      </SkyText>
    );
  }
  return (
    <SkyAccordion
      multiple
      openItems={openItems}
      onToggle={(_value, items) => onToggle(items)}
      testId="transformation-tests-groups"
    >
      {groups.map((group) => (
        <SkyAccordionItem
          key={group.key}
          value={group.key}
          heading={
            <span
              className="flex flex-wrap items-center gap-2"
              data-testid={`transformation-tests-heading-${group.label}`}
            >
              <span>{`${group.label} · ${group.members.length} ${
                group.members.length === 1 ? "test" : "tests"
              }${group.failing > 0 ? ` · ${group.failing} failing` : ""}`}</span>
              {groupBadge(group)}
            </span>
          }
        >
          <TestsGrid
            group={group}
            graph={graph}
            onQuery={onQuery}
            testId={`transformation-tests-group-${group.label}`}
          />
        </SkyAccordionItem>
      ))}
    </SkyAccordion>
  );
}

export function TransformationTestsView({
  graph,
  graphState,
  onRetry,
  canStart,
  transformations,
  onRefresh,
  onNewDbtJob,
}: TransformationTestsViewProps) {
  const [resultParam, setResultParam] = useLandingParam("result");
  const zone = useDisplayTz();
  const now = useNow();
  const [, setViewParam] = useLandingParam("view");
  const [runOpen, setRunOpen] = useState(false);
  const [sqlTarget, setSqlTarget] = useState<TransformationSqlTarget | null>(null);
  const lastRuns = useDbtJobRuns(1, canStart);
  const selected = testResultParam(resultParam);

  const views = useMemo<TestItem[]>(
    () =>
      graph
        ? graph.tests.map((test) => {
            const view = viewOf(test, graph);
            return {
              ...view,
              uniqueId: test.uniqueId,
              name: test.name,
              dependsOn: test.dependsOn,
              kind: test.kind,
            };
          })
        : [],
    [graph],
  );
  // D5: the landing's Tests fact reads the same tally (`testTally`), so the two cannot disagree.
  const counts = useMemo(() => testTally(graph?.tests ?? []), [graph]);
  const groups = useMemo(() => {
    if (!graph) return [];
    const shown = selected ? views.filter((view) => testResultOf(view.status) === selected) : views;
    return groupTests(shown, graph.nodes, (view) => testResultOf(view.status));
  }, [graph, views, selected]);
  const defaultOpen = useMemo(
    () => (groups.length === 1 ? groups.map((group) => group.key) : []),
    [groups],
  );
  const [openState, setOpenState] = useState<{ by: string; items: string[] } | null>(null);
  const stateKey = selected ?? "all";
  const openItems = openState && openState.by === stateKey ? openState.items : defaultOpen;

  const withTests = transformations.filter((row) => row.enabled && row.runTests);
  const newestResult = views
    .map((view) => view.at)
    .filter((at): at is string => Boolean(at))
    .sort(byCodeUnit)
    .at(-1);

  if (graphState === "loading" || (!graph && graphState !== "error")) {
    return <GraphLoading testId="transformation-tests-loading" />;
  }
  if (graphState === "error" || !graph) {
    return (
      <div data-testid="transformation-tests-view">
        <GraphReadError onRetry={onRetry} testId="transformation-tests-error" />
      </div>
    );
  }

  const runTestsControl =
    canStart && withTests.length > 0 ? (
      <SkyButton
        appearance="secondary"
        size="small"
        icon={PlayIcon}
        onClick={() => setRunOpen(true)}
        testId="transformation-tests-run"
      >
        Run tests
      </SkyButton>
    ) : null;

  const footer = (
    <div className="flex flex-col gap-1" data-testid="transformation-tests-footer">
      <SkyText size={200} color="muted">
        {"Tests run after their dbt job's models when Run tests is on. "}
        <SkyButton
          appearance="transparent"
          size="small"
          onClick={() => setViewParam("transformations", { result: null })}
          testId="transformation-tests-to-transformations"
        >
          Jobs
        </SkyButton>
      </SkyText>
      {withTests.length === 0 ? (
        <SkyText size={200} color="muted" testId="transformation-tests-none-on">
          Turn on Run tests on a dbt job to run its tests.
        </SkyText>
      ) : null}
    </div>
  );

  const empty =
    graph.run === null || graph.project === null ? (
      <ProjectEmptyState
        graph={graph}
        noun="tests"
        canStart={canStart}
        onRetry={onRetry}
        onNewDbtJob={onNewDbtJob}
        testId="transformation-tests-empty"
      />
    ) : null;

  return (
    <div className="flex flex-col gap-3" data-testid="transformation-tests-view">
      {empty ?? (
        <>
          <WalkedBackNote graph={graph} testId="transformation-tests-walked-back" />
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <SkyText size={200} color="muted" testId="transformation-tests-header">
              {resultsHeader(newestResult, zone, now)}
            </SkyText>
            <span className="ml-auto flex items-center gap-2">
              {runTestsControl}
              <SkyButton
                appearance="subtle"
                size="small"
                icon={RetryIcon}
                onClick={onRefresh}
                testId="transformation-tests-refresh"
              >
                Refresh
              </SkyButton>
            </span>
          </div>

          {views.length === 0 ? (
            <SkyText size={200} color="muted" testId="transformation-tests-no-tests">
              No test ran in the last 30 runs. Turn on Run tests on a dbt job to run its tests.
            </SkyText>
          ) : (
            <>
              <fieldset
                aria-label="Filter tests by result"
                className="flex min-w-0 flex-wrap items-center gap-2"
                data-testid="transformation-tests-summary"
              >
                <SkyButton
                  appearance={selected === null ? "secondary" : "subtle"}
                  size="small"
                  aria-pressed={selected === null}
                  onClick={() => setResultParam(null)}
                  testId="transformation-tests-summary-total"
                >
                  Total
                  <CountPill
                    count={views.length}
                    testId="transformation-tests-summary-total-count"
                  />
                </SkyButton>
                {SUMMARY.map((result) => (
                  <SkyButton
                    key={result}
                    appearance={selected === result ? "secondary" : "subtle"}
                    size="small"
                    aria-pressed={selected === result}
                    onClick={() => setResultParam(selected === result ? null : result)}
                    testId={`transformation-tests-summary-${result}`}
                  >
                    {RESULT_LABELS[result]}
                    <CountPill
                      count={counts[result]}
                      testId={`transformation-tests-summary-${result}-count`}
                    />
                  </SkyButton>
                ))}
              </fieldset>

              <TestGroups
                groups={groups}
                selected={selected}
                openItems={openItems}
                onToggle={(items) => setOpenState({ by: stateKey, items })}
                graph={graph}
                onQuery={(target, opener) => setSqlTarget({ ...target, opener })}
              />
            </>
          )}
        </>
      )}

      {footer}

      {runOpen ? (
        <RunDbtJobsDialog
          onOpenChange={setRunOpen}
          transformations={transformations}
          bundle={graph.bundle}
          runInFlight={newestInFlight(lastRuns.data?.rows[0])}
          initialSelection={withTests.map((row) => row.id)}
        />
      ) : null}
      {sqlTarget ? (
        <TransformationSqlDialog target={sqlTarget} onClose={() => setSqlTarget(null)} />
      ) : null}
    </div>
  );
}
